import { ApolloProvider as ApolloHooksProvider, useMutation, useQuery } from "@apollo/client/react";
import { userSettingsReducer } from 'components/UserSettings/containers/UserSettingsContainer'
import { adminTerminalsReducer } from 'containers/AdminTerminals/AdminTerminalsReducer'
import { jwtDecode } from 'jwt-decode'
import appLayoutReducer, {
  AppLayoutContainer,
} from 'layouts/AppLayout/containers/AppLayoutContainer'
import { CoreLayout } from 'layouts/CoreLayout/CoreLayout'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter, Routes, Route } from 'react-router'
import { getLocalStorageToken, setLocalStorage } from 'store/localStorage'
import { injectReducer } from 'store/reducers'
import mutationRefreshToken from '../graphql/mutations/mutationRefreshToken'
import { organisationsReducer } from './AdminOrganisations/organisationsReducer'
import { adminUsersReducer } from './AdminUsers/adminUsersReducer'
import { AppState } from 'models/AppState'
import LoginContainer from './Login/LoginContainer'
import { loginReducer, LOGIN_REMOVE_TOKEN } from './Login/loginReducer'
import notificationsReducer from './Notifications/notificationsReducer'
import { ipOrderRuleWizardReducer } from './RuleNewWizardContainer/RuleNewWizardReducer'
import queryCustomerSettings from 'graphql/queries/queryCustomerSettings'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CustomerSettings } from 'graphql/queries/types/CustomerSettings'
import { FleximeLoader } from 'components/FleximeLoader/FleximeLoader'
import { tokenRefreshDelay } from 'utils/tokenRefreshDelay'
import {
  refreshToken as refreshTokenTypes,
  refreshTokenVariables,
} from 'graphql/mutations/types/refreshToken'

interface AppContainerWrapperProps {
  store: any
  client: any
}

const AppContainerWrapper: React.FC<AppContainerWrapperProps> = ({ store, client }) => {
  const reducersInjected = useRef(false)
  if (!reducersInjected.current) {
    injectReducer(store, { key: 'notifications', reducer: notificationsReducer })
    injectReducer(store, { key: 'ipOrderRuleWizard', reducer: ipOrderRuleWizardReducer })
    injectReducer(store, { key: 'admin', reducer: adminUsersReducer })
    injectReducer(store, { key: 'organisations', reducer: organisationsReducer })
    injectReducer(store, { key: 'adminTerminals', reducer: adminTerminalsReducer })
    injectReducer(store, { key: 'appLayout', reducer: appLayoutReducer })
    injectReducer(store, { key: 'userSettings', reducer: userSettingsReducer })
    injectReducer(store, { key: 'login', reducer: loginReducer })
    reducersInjected.current = true
  }

  return (
    <ApolloHooksProvider client={client}>
      <div style={{ height: '100vh' }}>
        <BrowserRouter>
          <AppContainer />
        </BrowserRouter>
      </div>
    </ApolloHooksProvider>
  )
}

export default AppContainerWrapper

const AppContainer: React.FC = () => {
  const dispatch = useDispatch()
  const loginToken = useSelector((state: AppState) => state.login.token)
  const logout = useCallback(() => dispatch({ type: LOGIN_REMOVE_TOKEN }), [dispatch])

  const [loading, setLoading] = useState(true)
  const logoutCheck = useCallback(() => {
    try {
      const token = jwtDecode<{ exp?: number }>(getLocalStorageToken())
      if (token.exp && token.exp * 1000 < new Date().valueOf()) {
        logout()
      }
    } catch {
      logout()
    }
  }, [logout])
  useEffect(() => {
    logoutCheck()
  }, [logoutCheck])
  const { data: queryData } = useQuery<CustomerSettings>(queryCustomerSettings)
  const [refreshToken] = useMutation<refreshTokenTypes, refreshTokenVariables>(
    mutationRefreshToken,
  )

  const { SSO, emailLogin } = queryData?.customerSettings ?? {}
  const timeoutId = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshInProgress = useRef(false)
  const calculateTimeoutTime = useCallback(async (): Promise<number | null> => {
    try {
      return tokenRefreshDelay(jwtDecode<{ exp?: number; iat?: number }>(getLocalStorageToken()), Date.now())
    } catch {
      return null
    }
  }, [])
  const callback = useCallback(async () => {
    if (refreshInProgress.current) return
    refreshInProgress.current = true
    try {
      let token = getLocalStorageToken()
      const tokenData = await refreshToken({
        variables: { token },
      })
      if (tokenData?.data?.refreshToken) {
        setLocalStorage('token', tokenData?.data.refreshToken)
      }
      token = getLocalStorageToken()
      const timeout = await calculateTimeoutTime()
      if (token && timeout) {
        timeoutId.current = setTimeout(callback, timeout)
      }
    } catch {
      logoutCheck()
    } finally {
      refreshInProgress.current = false
    }
  }, [calculateTimeoutTime, refreshToken, logoutCheck])
  const startTimer = useCallback(async () => {
    try {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
        timeoutId.current = null
      }
      const token = getLocalStorageToken()
      const timeout = await calculateTimeoutTime()
      if (token && timeout) {
        timeoutId.current = setTimeout(callback, timeout)
      }
    } catch (err) {
      console.error('Failed to start token refresh timer:', err)
    }
  }, [calculateTimeoutTime, callback])
  const blur = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
  }, [])
  const focus = useCallback(async () => {
    logoutCheck()
    startTimer()
  }, [logoutCheck, startTimer])
  useEffect(() => {
    window.addEventListener('blur', blur)
    window.addEventListener('focus', focus)
    startTimer()
    setLoading(false)
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
      window.removeEventListener('blur', blur)
      window.removeEventListener('focus', focus)
    }
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getLocalStorageToken(),
    blur,
    calculateTimeoutTime,
    callback,
    focus,
    logoutCheck,
    refreshToken,
    startTimer,
  ])
  if (loading) {
    return (
      <div className="w-full h-full flex-1 flex-center">
        <FleximeLoader size={64} />
      </div>
    )
  }
  return (
    <>
      {!loginToken ? (
        <Routes>
          <Route path="*" element={<LoginContainer SSO={SSO ?? false} emailLogin={emailLogin ?? false} />} />
        </Routes>
      ) : (
        <CoreLayout>
          <AppLayoutContainer />
        </CoreLayout>
      )}
    </>
  )
}
